/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `training_file` message.
 *
 * Corresponds to file_id of workout or course.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class TrainingFileMesg : Mesg {
    public constructor() : super("TrainingFile", Profile.MesgNum.TRAINING_FILE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** type. */
    public var type: File?
        get() = (getFieldValue(TYPE_FIELD_NUM) as? UByte)?.let { File.fromValue(it) }
        set(value) = setFieldValue(TYPE_FIELD_NUM, value?.value)

    /** manufacturer. */
    public var manufacturer: Manufacturer?
        get() = (getFieldValue(MANUFACTURER_FIELD_NUM) as? UShort)?.let { Manufacturer.fromValue(it) }
        set(value) = setFieldValue(MANUFACTURER_FIELD_NUM, value?.value)

    /** product. */
    public var product: UShort?
        get() = getFieldValue(PRODUCT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PRODUCT_FIELD_NUM, value)

    /**
     * favero_product: what product means when this message selects it.
     */
    public val faveroProduct: FaveroProduct?
        get() = (getFieldValue(PRODUCT_FIELD_NUM, 0, SubFieldSelector.Named("FaveroProduct")) as? UShort)?.let { FaveroProduct.fromValue(it) }

    public fun setFaveroProduct(value: FaveroProduct?) {
        setFieldValue(PRODUCT_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("FaveroProduct"))
    }

    /**
     * garmin_product: what product means when this message selects it.
     */
    public val garminProduct: GarminProduct?
        get() = (getFieldValue(PRODUCT_FIELD_NUM, 0, SubFieldSelector.Named("GarminProduct")) as? UShort)?.let { GarminProduct.fromValue(it) }

    public fun setGarminProduct(value: GarminProduct?) {
        setFieldValue(PRODUCT_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("GarminProduct"))
    }

    /** serial_number. */
    public var serialNumber: UInt?
        get() = getFieldValue(SERIAL_NUMBER_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SERIAL_NUMBER_FIELD_NUM, value)

    /** time_created. */
    public var timeCreated: Instant?
        get() = (getFieldValue(TIME_CREATED_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIME_CREATED_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TYPE_FIELD_NUM: UByte = 0u
        public const val MANUFACTURER_FIELD_NUM: UByte = 1u
        public const val PRODUCT_FIELD_NUM: UByte = 2u
        public const val SERIAL_NUMBER_FIELD_NUM: UByte = 3u
        public const val TIME_CREATED_FIELD_NUM: UByte = 4u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("Type", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("Manufacturer", 1u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[2] = Field("Product", 2u, BaseType.UINT16, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("FaveroProduct", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(1u, 263)
                    },
                )
                .addSubField(
                    SubField("GarminProduct", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(1u, 1)
                        subField.addMap(1u, 15)
                        subField.addMap(1u, 13)
                        subField.addMap(1u, 89)
                    },
                )
            fields[3] = Field("SerialNumber", 3u, BaseType.UINT32Z, 1.0, 0.0, "", false)
            fields[4] = Field("TimeCreated", 4u, BaseType.UINT32, 1.0, 0.0, "", false)
            return fields
        }
    }
}
